import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OfferMultiSelectStyle } from './OfferMultiSelect.style';

export interface OfferMultiSelectOption {
  value: string;
  label: string;
}

export interface OfferMultiSelectProps {
  id?: string;
  options: OfferMultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  /** Max number of chips shown before "+N more" */
  maxChipCount?: number;
  /** Max selectable values (omit for unlimited) */
  maxSelected?: number;
  /** If this value is selected, clear others and block combining (e.g. "I don't know") */
  exclusiveValue?: string;
  emptyText?: string;
  searchPlaceholder?: string;
}

/**
 * Custom multi-select — no Ant Design Select.
 * Inline dropdown panel (not a portal) so it works reliably inside modals.
 */
const OfferMultiSelect: React.FC<OfferMultiSelectProps> = ({
  id,
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
  error = false,
  maxChipCount = 4,
  maxSelected,
  exclusiveValue,
  emptyText = 'No options found',
  searchPlaceholder = 'Search…',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedSet = useMemo(() => new Set(value), [value]);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach(opt => map.set(opt.value, opt.label));
    return map;
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      opt =>
        opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
        setQuery('');
        onBlur?.();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onBlur]);

  useEffect(() => {
    if (open) {
      // Focus search when panel opens
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const openPanel = () => {
    if (disabled || loading) return;
    setOpen(true);
  };

  const toggleValue = (optionValue: string) => {
    if (disabled || loading) return;

    // Exclusive option (e.g. "I don't know")
    if (exclusiveValue && optionValue === exclusiveValue) {
      if (selectedSet.has(exclusiveValue)) {
        onChange([]);
      } else {
        onChange([exclusiveValue]);
      }
      return;
    }

    // Selecting a normal option while exclusive is selected → drop exclusive
    let next = value.filter(v => v !== exclusiveValue);

    if (selectedSet.has(optionValue)) {
      next = next.filter(v => v !== optionValue);
    } else {
      if (typeof maxSelected === 'number' && next.length >= maxSelected) {
        return;
      }
      next = [...next, optionValue];
    }

    onChange(next);
  };

  const removeValue = (optionValue: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || loading) return;
    onChange(value.filter(v => v !== optionValue));
  };

  const visibleValues = value.slice(0, maxChipCount);
  const hiddenCount = Math.max(0, value.length - maxChipCount);

  const allSelected =
    options.length > 0 &&
    options.every(opt => selectedSet.has(opt.value)) &&
    !exclusiveValue;

  const selectAllVisible = () => {
    if (disabled || loading) return;
    if (exclusiveValue) {
      // Don't bulk-select when exclusive mode exists
      const withoutExclusive = filteredOptions
        .map(o => o.value)
        .filter(v => v !== exclusiveValue);
      const merged = Array.from(new Set([...value.filter(v => v !== exclusiveValue), ...withoutExclusive]));
      onChange(
        typeof maxSelected === 'number' ? merged.slice(0, maxSelected) : merged
      );
      return;
    }
    const merged = Array.from(
      new Set([...value, ...filteredOptions.map(o => o.value)])
    );
    onChange(typeof maxSelected === 'number' ? merged.slice(0, maxSelected) : merged);
  };

  const clearAll = () => {
    if (disabled || loading) return;
    onChange([]);
  };

  const selectAllLabel =
    typeof maxSelected === 'number'
      ? `Select up to ${maxSelected}${query ? ' shown' : ''}`
      : `Select all${query ? ' filtered' : ''}`;

  const showBulkSelect = !exclusiveValue || typeof maxSelected === 'number';

  return (
    <OfferMultiSelectStyle
      ref={rootRef}
      className={[
        'offer-multi-select',
        open ? 'is-open' : '',
        error ? 'is-error' : '',
        disabled || loading ? 'is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        id={id}
        className="offer-multi-select__control"
        disabled={disabled || loading}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (open) {
            setOpen(false);
            setQuery('');
            onBlur?.();
          } else {
            openPanel();
          }
        }}
      >
        <div className="offer-multi-select__chips">
          {value.length === 0 ? (
            <span className="offer-multi-select__placeholder">
              {loading ? 'Loading…' : placeholder}
            </span>
          ) : (
            <>
              {visibleValues.map(val => (
                <span key={val} className="offer-multi-select__chip">
                  <span className="offer-multi-select__chip-label" title={labelByValue.get(val) || val}>
                    {labelByValue.get(val) || val}
                  </span>
                  <span
                    className="offer-multi-select__chip-remove"
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove ${labelByValue.get(val) || val}`}
                    onClick={e => removeValue(val, e)}
                    onKeyDown={() => undefined}
                  >
                    ×
                  </span>
                </span>
              ))}
              {hiddenCount > 0 ? (
                <span className="offer-multi-select__chip offer-multi-select__chip--more">
                  +{hiddenCount} more
                </span>
              ) : null}
            </>
          )}
        </div>
        <span className="offer-multi-select__caret" aria-hidden>
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open ? (
        <div className="offer-multi-select__panel" role="listbox" aria-multiselectable>
          <div className="offer-multi-select__search-row">
            <input
              ref={searchRef}
              type="text"
              className="offer-multi-select__search"
              value={query}
              placeholder={searchPlaceholder}
              disabled={disabled || loading}
              onChange={e => setQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            />
          </div>

          <div className="offer-multi-select__actions">
            {showBulkSelect ? (
              <button type="button" className="offer-multi-select__action" onClick={selectAllVisible}>
                {selectAllLabel}
              </button>
            ) : null}
            <button type="button" className="offer-multi-select__action" onClick={clearAll}>
              Clear
            </button>
            {typeof maxSelected === 'number' ? (
              <span className="offer-multi-select__count">
                {value.length}/{maxSelected}
              </span>
            ) : (
              <span className="offer-multi-select__count">
                {value.length} selected
                {allSelected ? ' (all)' : ''}
              </span>
            )}
          </div>

          <div className="offer-multi-select__list">
            {filteredOptions.length === 0 ? (
              <div className="offer-multi-select__empty">{emptyText}</div>
            ) : (
              filteredOptions.map(opt => {
                const checked = selectedSet.has(opt.value);
                const isExclusive = exclusiveValue === opt.value;
                const blockedByExclusive =
                  !!exclusiveValue &&
                  selectedSet.has(exclusiveValue) &&
                  !isExclusive;
                const blockedByMax =
                  !checked &&
                  typeof maxSelected === 'number' &&
                  value.filter(v => v !== exclusiveValue).length >= maxSelected &&
                  !isExclusive;

                const optionDisabled = blockedByExclusive || blockedByMax;

                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={[
                      'offer-multi-select__option',
                      checked ? 'is-selected' : '',
                      optionDisabled ? 'is-disabled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={optionDisabled}
                    onClick={() => toggleValue(opt.value)}
                  >
                    <span className={`offer-multi-select__check ${checked ? 'is-on' : ''}`}>
                      {checked ? '✓' : ''}
                    </span>
                    <span className="offer-multi-select__option-label">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </OfferMultiSelectStyle>
  );
};

export default OfferMultiSelect;
